import { faClock, faMapMarkerAlt } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import React, { useMemo } from "react";
import { RRule } from "rrule";
import Helper from "../../../Helper";
import { RepeatingCalendarEventDefinition } from "../../../models/RepeatingCalendarEventDefinition";
import { contiunousSequenceFromStartAndEnd, rRuleDaysToDayNums } from "../../Calendar/helper";

interface JobsiteInformationCardProps {
    project: any;
}

const JobsiteInformationCard: React.FC<JobsiteInformationCardProps> = ({ project }) => {
    const openHoursDefinition = useMemo(() => {
        const repeatingCalendarEventDefinitions = project.repeating_calendar_event_definitions || [];

        const def = repeatingCalendarEventDefinitions.find((def: any) => def.name === "Project Hours");

        if (def) {
            return RepeatingCalendarEventDefinition.fromApiObject(def, project.timezone);
        }

        return null;
    }, [project]);

    const deliveryHoursAllDay = useMemo(() => {
        if (!openHoursDefinition) {
            return false;
        }

        // If the hours are multiples of 24 hours, then we know they are all day
        if ((openHoursDefinition.duration / 60) % 24 === 0) {
            return true;
        }

        return false;
    }, [openHoursDefinition]);

    const workWeekDaysArray = useMemo(() => {
        if (!openHoursDefinition) {
            return [];
        }

        if (deliveryHoursAllDay) {
            const startDay = openHoursDefinition.startAt?.get("day");

            if (startDay === undefined) {
                return [];
            }

            const endDay = (startDay + openHoursDefinition.duration / (24 * 60) - 1) % 7;

            return contiunousSequenceFromStartAndEnd(startDay, endDay);
        }

        const rrule = RRule.fromString(openHoursDefinition?.rrule);

        return rRuleDaysToDayNums(rrule.options.byweekday as number[]);
    }, [project, deliveryHoursAllDay, openHoursDefinition]);

    const workWeekDays = () => {
        const defaultString = "Jobsite Work Week Not Set";

        if (!openHoursDefinition || workWeekDaysArray.length === 0) {
            return defaultString;
        }

        return `${dayjs().day(workWeekDaysArray[0]).format("dddd")} - ${dayjs()
            .day(workWeekDaysArray.at(-1) || 6)
            .format("dddd")}`;
    };

    const deliveryHours = () => {
        if (!openHoursDefinition || deliveryHoursAllDay) {
            return "All Day";
        }

        const start = openHoursDefinition.startAt;
        const end = dayjs(start).add(openHoursDefinition.duration, "minute").toDate();

        return `${dayjs(start).format("h:mm A")} - ${dayjs(end).format("h:mm A")}`;
    };

    if (!project) {
        return <div>Loading...</div>;
    }

    return (
        <div className="flex flex-col gap-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 rounded-lg shadow-sm border bg-gray-50 dark:bg-neutral-800 border-gray-200 dark:border-neutral-700">
                {/* Jobsite Information */}
                <div>
                    <div className="flex items-start align-top justify-start gap-2">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="text-blue-600" />
                        <div className="flex flex-col gap-1">
                            <h4 className="font-medium text-gray-900 dark:text-neutral-100">Project Address</h4>
                            {Helper.addressFormater(project)
                                .split(",")
                                .map((part: string, idx: number) => (
                                    <div className="text-gray-600 dark:text-neutral-300" key={idx}>
                                        {part.trim()}
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
                {/* Delivery Hours */}
                <div>
                    <div className="flex items-start align-top justify-start gap-2">
                        <FontAwesomeIcon icon={faClock} className="text-blue-600" />
                        <div className="flex flex-col gap-1">
                            <h4 className="font-medium text-gray-900 dark:text-neutral-100">Delivery Hours</h4>
                            <p className="text-gray-600 dark:text-neutral-300">{workWeekDays()}</p>
                            <p className="text-gray-600 dark:text-neutral-300">{deliveryHours()}</p>
                        </div>
                    </div>
                </div>
                {/* <JobsiteContactCard contact={undefined} /> */}
                {/* <JobsiteContactCard contact={undefined} /> */}
            </div>
        </div>
    );
};

export default JobsiteInformationCard;
